import { useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import "./AccountAddresses.css";
import {
  getDireccionesCliente,
  createDireccionCliente,
  updateDireccionCliente,
  deleteDireccionCliente,
} from "../../services/usuarios_cliente/perfildirecciones";
import { getPaises, getRegiones, getCiudades } from "../../services/locacion";

import { getErrMsg, norm, shallowEqualByKeys } from "../../utils/accountUtils";

const numOrEmpty = (v) => {
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : "";
};

export default function AccountAddresses() {
  const { addresses, setAddresses, pushToast } = useOutletContext();

  const [editingAddress, setEditingAddress] = useState(null);
  const [savingAddress, setSavingAddress] = useState(false);

  const baselineRef = useRef(null);

  const [paises, setPaises] = useState([]);
  const [regiones, setRegiones] = useState([]);
  const [ciudades, setCiudades] = useState([]);
  const [cardLocationLines, setCardLocationLines] = useState({});

  const emptyAddress = {
    cliente_direccion_id: null,
    nombre_destinatario: "",
    direccion_linea_1: "",
    direccion_linea_2: "",
    pais_id: "",
    region_id: "",
    ciudad_id: "",
    referencia: "",
    codigo_postal: "",
    direccion_principal: false,
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await getPaises();
        if (!cancelled) setPaises(Array.isArray(p) ? p : []);
      } catch (error) {
        if (!cancelled) {
          setPaises([]);
          pushToast(
            "error",
            "Ubicación",
            getErrMsg(error, "No pudimos cargar la lista de países.")
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // pushToast estable en la práctica; evitar deps para no re-disparar al cambiar la referencia del padre
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!addresses?.length) {
      setCardLocationLines({});
      return;
    }
    let cancelled = false;
    (async () => {
      const next = {};
      await Promise.all(
        addresses.map(async (a) => {
          const id = a.cliente_direccion_id;
          if (!id) return;
          try {
            const pNombre =
              paises.find((p) => p.pais_id === a.pais_id)?.nombre || "";
            const regs = await getRegiones(a.pais_id);
            const rNombre =
              regs.find((r) => r.region_id === a.region_id)?.nombre || "";
            const cits = await getCiudades(a.region_id);
            const cNombre =
              cits.find((c) => c.ciudad_id === a.ciudad_id)?.nombre || "";
            next[id] = [pNombre, rNombre, cNombre].filter(Boolean).join(" · ");
          } catch {
            next[id] = "";
          }
        })
      );
      if (!cancelled) setCardLocationLines(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [addresses, paises]);

  useEffect(() => {
    let cancelled = false;
    const pid = editingAddress?.pais_id;
    if (pid === "" || pid == null) {
      setRegiones([]);
      return;
    }
    (async () => {
      try {
        const r = await getRegiones(pid);
        if (!cancelled) setRegiones(Array.isArray(r) ? r : []);
      } catch {
        if (!cancelled) setRegiones([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editingAddress?.pais_id]);

  useEffect(() => {
    let cancelled = false;
    const rid = editingAddress?.region_id;
    if (rid === "" || rid == null) {
      setCiudades([]);
      return;
    }
    (async () => {
      try {
        const c = await getCiudades(rid);
        if (!cancelled) setCiudades(Array.isArray(c) ? c : []);
      } catch {
        if (!cancelled) setCiudades([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editingAddress?.region_id]);

  const addNewAddress = () => {
    const next = { ...emptyAddress };
    baselineRef.current = { ...next };
    setEditingAddress(next);
  };

  const editAddress = (addr) => {
    const next = {
      ...addr,
      pais_id: numOrEmpty(addr.pais_id),
      region_id: numOrEmpty(addr.region_id),
      ciudad_id: numOrEmpty(addr.ciudad_id),
      referencia: addr.referencia != null ? String(addr.referencia) : "",
      codigo_postal: addr.codigo_postal != null ? String(addr.codigo_postal) : "",
    };
    baselineRef.current = { ...next };
    setEditingAddress(next);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditingAddress((a) =>
      a ? { ...a, [name]: type === "checkbox" ? checked : value } : a
    );
  };

  const handlePaisChange = (e) => {
    const v = e.target.value;
    setEditingAddress((a) =>
      a
        ? {
            ...a,
            pais_id: v === "" ? "" : Number(v),
            region_id: "",
            ciudad_id: "",
          }
        : a
    );
  };

  const handleRegionChange = (e) => {
    const v = e.target.value;
    setEditingAddress((a) =>
      a
        ? {
            ...a,
            region_id: v === "" ? "" : Number(v),
            ciudad_id: "",
          }
        : a
    );
  };

  const handleCiudadChange = (e) => {
    const v = e.target.value;
    setEditingAddress((a) =>
      a ? { ...a, ciudad_id: v === "" ? "" : Number(v) } : a
    );
  };

  const isDirty = useMemo(() => {
    if (!editingAddress) return false;
    const base = baselineRef.current;
    if (!base) return true;

    const keys = [
      "nombre_destinatario",
      "direccion_linea_1",
      "direccion_linea_2",
      "pais_id",
      "region_id",
      "ciudad_id",
      "referencia",
      "codigo_postal",
      "direccion_principal",
    ];

    return !shallowEqualByKeys(base, editingAddress, keys);
  }, [editingAddress]);

  const isValid = useMemo(() => {
    if (!editingAddress) return false;

    const a = editingAddress;
    const p = numOrEmpty(a.pais_id);
    const r = numOrEmpty(a.region_id);
    const c = numOrEmpty(a.ciudad_id);

    return (
      norm(a.nombre_destinatario) !== "" &&
      norm(a.direccion_linea_1) !== "" &&
      p !== "" &&
      r !== "" &&
      c !== ""
    );
  }, [editingAddress]);

  const refreshAddresses = async () => {
    const updated = await getDireccionesCliente();
    const list = Array.isArray(updated?.data) ? updated.data : [];
    setAddresses(list);
  };

  const saveAddress = async () => {
    if (!editingAddress) return;

    if (!isDirty) {
      pushToast("info", "Sin cambios", "No hay nada nuevo para guardar.");
      return;
    }

    if (!isValid) {
      pushToast(
        "warn",
        "Faltan campos",
        "Por favor completa todos los campos requeridos."
      );
      return;
    }

    setSavingAddress(true);
    try {
      const p = numOrEmpty(editingAddress.pais_id);
      const r = numOrEmpty(editingAddress.region_id);
      const c = numOrEmpty(editingAddress.ciudad_id);

      const payload = {
        nombre_destinatario: norm(editingAddress.nombre_destinatario),
        direccion_linea_1: norm(editingAddress.direccion_linea_1),
        direccion_linea_2: norm(editingAddress.direccion_linea_2) || null,
        pais_id: p,
        region_id: r,
        ciudad_id: c,
        codigo_postal: norm(editingAddress.codigo_postal) || null,
        referencia: norm(editingAddress.referencia) || null,
        direccion_principal: !!editingAddress.direccion_principal,
      };

      if (editingAddress.cliente_direccion_id) {
        await updateDireccionCliente(editingAddress.cliente_direccion_id, payload);
      } else {
        await createDireccionCliente(payload);
      }

      await refreshAddresses();

      setEditingAddress(null);
      baselineRef.current = null;

      pushToast("success", "Dirección guardada", "Tu dirección se guardó correctamente.");
    } catch (error) {
      pushToast("error", "Error al guardar", getErrMsg(error, "No pudimos guardar tu dirección."));
    } finally {
      setSavingAddress(false);
    }
  };

  const removeAddress = async (id) => {
    try {
      await deleteDireccionCliente(id);
      await refreshAddresses();
      pushToast("success", "Dirección eliminada", "Tu dirección fue eliminada.");
    } catch (error) {
      pushToast("error", "Error al eliminar", getErrMsg(error, "No pudimos eliminar tu dirección."));
    }
  };

  return (
    <div className="adc-card adc-addresses">
      <div className="adc-address-header">
        <div>
          <h2 className="adc-address-title">Direcciones</h2>
          <p className="muted">Administra tus ubicaciones de entrega.</p>
        </div>

        <button type="button" className="adc-btn-primary" onClick={addNewAddress}>
          Agregar dirección
        </button>
      </div>

      {(!addresses || addresses.length === 0) && !editingAddress && (
        <p className="muted">Aún no has guardado direcciones.</p>
      )}

      <div className="adc-address-grid">
        {(addresses || []).map((a) => (
          <div key={a.cliente_direccion_id} className="adc-address-card">
            <div className="adc-address-body">
              <div className="adc-address-nameRow">
                <strong>{a.nombre_destinatario}</strong>
                {a.direccion_principal ? (
                  <span className="adc-badge adc-badge--tiny">Principal</span>
                ) : null}
              </div>

              <p className="muted">
                {a.direccion_linea_1}
                {a.direccion_linea_2 ? `, ${a.direccion_linea_2}` : ""}
              </p>
              {cardLocationLines[a.cliente_direccion_id] ? (
                <p className="muted">{cardLocationLines[a.cliente_direccion_id]}</p>
              ) : null}
              {a.codigo_postal ? (
                <p className="muted">C.P. {a.codigo_postal}</p>
              ) : null}
              {a.referencia ? <p className="muted">{a.referencia}</p> : null}
            </div>

            <div className="adc-address-actions">
              <button type="button" className="adc-btn-edit" onClick={() => editAddress(a)}>
                Editar
              </button>
              <button
                type="button"
                className="adc-btn-delete"
                onClick={() => removeAddress(a.cliente_direccion_id)}
              >
                Eliminar
              </button>
            </div>
          </div>
        ))}
      </div>

      {editingAddress && (
        <div className="adc-address-form">
          <h3 className="adc-address-formTitle">
            {editingAddress.cliente_direccion_id ? "Editar dirección" : "Nueva dirección"}
          </h3>

          <div className="adc-form-grid">
            <div className="adc-field">
              <label>Nombre del destinatario</label>
              <input
                name="nombre_destinatario"
                value={editingAddress.nombre_destinatario}
                onChange={handleChange}
              />
            </div>

            <div className="adc-field">
              <label>Dirección línea 1</label>
              <input
                name="direccion_linea_1"
                value={editingAddress.direccion_linea_1}
                onChange={handleChange}
              />
            </div>

            <div className="adc-field">
              <label>Dirección línea 2</label>
              <input
                name="direccion_linea_2"
                value={editingAddress.direccion_linea_2}
                onChange={handleChange}
              />
            </div>

            <div className="adc-field">
              <label>País</label>
              <select
                name="pais_id"
                value={editingAddress.pais_id === "" ? "" : String(editingAddress.pais_id)}
                onChange={handlePaisChange}
              >
                <option value="">Selecciona un país</option>
                {paises.map((p) => (
                  <option key={p.pais_id} value={p.pais_id}>
                    {p.nombre}
                  </option>
                ))}
              </select>
            </div>

            <div className="adc-field">
              <label>Región / departamento</label>
              <select
                name="region_id"
                value={editingAddress.region_id === "" ? "" : String(editingAddress.region_id)}
                onChange={handleRegionChange}
                disabled={editingAddress.pais_id === ""}
              >
                <option value="">
                  {editingAddress.pais_id === "" ? "Primero elige un país" : "Selecciona región / departamento"}
                </option>
                {regiones.map((r) => (
                  <option key={r.region_id} value={r.region_id}>
                    {r.nombre}
                  </option>
                ))}
              </select>
            </div>

            <div className="adc-field">
              <label>Ciudad / municipio</label>
              <select
                name="ciudad_id"
                value={editingAddress.ciudad_id === "" ? "" : String(editingAddress.ciudad_id)}
                onChange={handleCiudadChange}
                disabled={editingAddress.region_id === ""}
              >
                <option value="">
                  {editingAddress.region_id === ""
                    ? "Primero elige región / departamento"
                    : "Selecciona ciudad / municipio"}
                </option>
                {ciudades.map((c) => (
                  <option key={c.ciudad_id} value={c.ciudad_id}>
                    {c.nombre}
                  </option>
                ))}
              </select>
            </div>

            <div className="adc-field adc-field--full">
              <label>Referencia de la dirección</label>
              <textarea
                name="referencia"
                rows={3}
                value={editingAddress.referencia}
                onChange={handleChange}
              />
            </div>

            <div className="adc-field">
              <label>Código postal</label>
              <input
                name="codigo_postal"
                value={editingAddress.codigo_postal}
                onChange={handleChange}
              />
            </div>
          </div>

          <label className="adc-checkbox">
            <input
              type="checkbox"
              name="direccion_principal"
              checked={!!editingAddress.direccion_principal}
              onChange={handleChange}
            />
            Establecer como dirección principal
          </label>

          <div className="adc-form-actions">
            <button
              type="button"
              className="adc-btn-primary"
              onClick={saveAddress}
              disabled={savingAddress || !isDirty || !isValid}
              title={
                !isDirty
                  ? "No hay cambios para guardar"
                  : !isValid
                  ? "Completa los campos requeridos"
                  : ""
              }
            >
              {savingAddress ? "Guardando…" : "Guardar dirección"}
            </button>

            <button
              type="button"
              className="adc-btn-secondary"
              onClick={() => {
                setEditingAddress(null);
                baselineRef.current = null;
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
